import { gql } from "generated-graphql";

/**
 * Shared row shape for the container grids. The clock is engine-computed;
 * `effectiveDeadlineDate` mirrors the sortable `deadlineDate` column the
 * backend orders by.
 */
gql(`
  fragment WasteContainerListItem on WasteContainer {
    id
    facilityId
    containerLabel
    containerType
    capacity
    capacityUnit
    amount
    amountUnit
    amountUnavailable
    isEstimate
    isPermanent
    status
    accumulationStartDate
    accumulationArea {
      id
      name
      type
    }
    episodicEvent {
      id
      relief {
        isAvailable
      }
    }
    wasteStream {
      id
      name
      classification
      profile {
        id
        profileNumber
      }
    }
    clock {
      effectiveDeadlineDate
      status
      limits {
        limitType
        daysRemaining
        totalDays
        status
        basis
        citation
        jurisdiction
      }
    }
    createdAt
  }
`);

/**
 * Events a container created at this facility could join. The window rule on
 * the waste itself decides eligibility, so a lapsed event is still listed.
 */
export const FACILITY_JOINABLE_EPISODIC_EVENTS_QUERY = gql(`
  query FacilityJoinableEpisodicEvents($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      episodicEvents(status: ACTIVE) {
        items {
          id
          reason
          type
          startDate
          accumulationDeadline
          relief {
            isAvailable
            reason
          }
        }
      }
    }
  }
`);

export const TENANT_WASTE_CONTAINERS_QUERY = gql(`
  query TenantWasteContainers(
    $tenantId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tenant(id: $tenantId) {
      id
      wasteContainers(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          ...WasteContainerListItem
          facility {
            id
            name
          }
        }
        count
      }
    }
  }
`);

export const FACILITY_WASTE_CONTAINERS_QUERY = gql(`
  query FacilityWasteContainers(
    $facilityId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    facility(id: $facilityId) {
      id
      name
      wasteContainers(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          ...WasteContainerListItem
        }
        count
      }
    }
  }
`);

/** Name lookup for the facility-scope chip on the list page. */
export const WASTE_CONTAINER_FACILITY_NAME_QUERY = gql(`
  query WasteContainerFacilityName($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      name
    }
  }
`);

/** Facility dashboard tile: the active-container count plus the on-site
 * totals and cap headroom by generator status. */
export const FACILITY_ACCUMULATION_SUMMARY_QUERY = gql(`
  query FacilityAccumulationSummary($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      accumulationSummary {
        activeContainerCount
        approachingDeadlineContainerCount
        overdueContainerCount
        discrepantShipmentCount
        nonAcuteOnSiteKg
        acuteOnSiteKg
        onSiteCapKg
        acuteOnSiteCapKg
        overCap
        episodicExemptNonAcuteKg
        episodicExemptAcuteKg
        onSiteCapAppliesTo
        assumedDensityContainerCount
        needsWasteStreamContainerCount
        federalGeneratorStatus
        accumulationThresholdJudgedAgainst {
          abbreviation
          name
        }
      }
      activeEpisodicEvent {
        id
        reason
        accumulationDeadline
        relief {
          isAvailable
          reason
        }
      }
    }
  }
`);

gql(`
  fragment ContainerFillEventItem on ContainerFillEvent {
    id
    amount
    unit
    isEstimate
    occurredAt
    source
    note
    sourceContainerId
  }
`);

gql(`
  fragment ContainerEventItem on ContainerEvent {
    id
    type
    payload
    occurredAt
    actorId
  }
`);

export const GET_WASTE_CONTAINER_QUERY = gql(`
  query GetWasteContainer($id: ID!) {
    wasteContainer(id: $id) {
      id
      facilityId
      facility {
        id
        name
        defaultLabelFormat
      }
      containerLabel
      containerType
      capacity
      capacityUnit
      qrUrl
      isPermanent
      isFlowThrough
      amount
      amountUnit
      amountUnavailable
      isEstimate
      status
      countsTowardGeneration
      accumulationArea {
        id
        name
        type
      }
      wasteStream {
        id
        name
        classification
        profile {
          id
          physicalState
          profileNumber
          tsdfName
          specificGravity
        }
      }
      accumulationStartDate
      fullDate
      movedToCentralDate
      shippedDate
      estimatedPickupDate
      sqgOver200Miles
      episodicEvent {
        id
        reason
        startDate
        accumulationDeadline
        relief {
          isAvailable
          reason
        }
      }
      clock {
        limits {
          limitType
          basis
          citation
          jurisdiction
          deadlineDate
          daysRemaining
          totalDays
          status
        }
        effectiveDeadlineDate
        status
        undetermined {
          missingAxes
          reason
        }
      }
      fillEvents {
        ...ContainerFillEventItem
        runningTotal
      }
      events {
        ...ContainerEventItem
      }
      issues {
        ...issue
      }
      archivedAt
      openShipments {
        id
        manifestRequestNumber
        status
      }
      shippedOnShipment {
        id
        manifestRequestNumber
        processedManifestId
        manifestTrackingNumber
      }
      labelNote
      label {
        ...LabelContentFields
      }
      createdAt
      updatedAt
    }
  }
`);

export const VALIDATE_WASTE_CONTAINER_INPUT = gql(`
  query ValidateWasteContainerInput($input: WasteContainerCreateInput!, $currentContainerId: ID) {
    wasteContainerInputValidator(input: $input, currentContainerId: $currentContainerId) {
      ...issue
    }
  }
`);

export const CREATE_WASTE_CONTAINER_MUTATION = gql(`
  mutation CreateWasteContainer($input: WasteContainerCreateInput!) {
    createWasteContainer(input: $input) {
      ...WasteContainerListItem
    }
  }
`);

export const UPDATE_WASTE_CONTAINER_MUTATION = gql(`
  mutation UpdateWasteContainer($input: WasteContainerUpdateInput!) {
    updateWasteContainer(input: $input)
  }
`);

export const LOG_CONTAINER_FILL_EVENT_MUTATION = gql(`
  mutation LogContainerFillEvent($input: LogContainerFillEventInput!) {
    logContainerFillEvent(input: $input) {
      ...ContainerFillEventItem
    }
  }
`);

export const UPDATE_CONTAINER_FILL_EVENT_MUTATION = gql(`
  mutation UpdateContainerFillEvent($input: UpdateContainerFillEventInput!) {
    updateContainerFillEvent(input: $input) {
      ...ContainerFillEventItem
    }
  }
`);

export const DELETE_CONTAINER_FILL_EVENT_MUTATION = gql(`
  mutation DeleteContainerFillEvent($id: ID!) {
    deleteContainerFillEvent(id: $id)
  }
`);

export const SET_CONTAINER_AMOUNT_MUTATION = gql(`
  mutation SetContainerAmount($input: SetContainerAmountInput!) {
    setContainerAmount(input: $input) {
      ...ContainerFillEventItem
    }
  }
`);

export const EMPTY_CONTAINER_MUTATION = gql(`
  mutation EmptyContainer($input: EmptyContainerInput!) {
    emptyContainer(input: $input)
  }
`);

export const CONSOLIDATE_CONTAINERS_MUTATION = gql(`
  mutation ConsolidateContainers($input: ConsolidateContainersInput!) {
    consolidateContainers(input: $input)
  }
`);

export const SET_CONTAINER_STATUS_MUTATION = gql(`
  mutation SetContainerStatus($input: SetContainerStatusInput!) {
    setContainerStatus(input: $input)
  }
`);

export const VALIDATE_CONTAINER_FILL_EVENT_INPUT = gql(`
  query ValidateContainerFillEventInput($input: LogContainerFillEventInput!, $currentFillEventId: ID) {
    containerFillEventInputValidator(input: $input, currentFillEventId: $currentFillEventId) {
      ...issue
    }
  }
`);

export const VALIDATE_SET_CONTAINER_AMOUNT_INPUT = gql(`
  query ValidateSetContainerAmountInput($input: SetContainerAmountInput!) {
    setContainerAmountInputValidator(input: $input) {
      ...issue
    }
  }
`);

export const VALIDATE_CONSOLIDATE_CONTAINERS_INPUT = gql(`
  query ValidateConsolidateContainersInput($input: ConsolidateContainersInput!) {
    consolidateContainersInputValidator(input: $input) {
      ...issue
    }
  }
`);

export const VALIDATE_SET_CONTAINER_STATUS_INPUT = gql(`
  query ValidateSetContainerStatusInput($input: SetContainerStatusInput!) {
    setContainerStatusInputValidator(input: $input) {
      ...issue
    }
  }
`);

/** Options feed for the waste-stream picker in the add/edit form. */
export const FACILITY_WASTE_STREAM_OPTIONS_QUERY = gql(`
  query FacilityWasteStreamOptions(
    $facilityId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    facility(id: $facilityId) {
      id
      wasteStreams(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          id
          name
          classification
          profile {
            id
            profileNumber
            specificGravity
          }
        }
        count
      }
    }
  }
`);

/**
 * Options feed for the placement picker (create-mode area picker, move
 * target). The resolver already scopes to non-archived areas, so no
 * archivedAt filter is needed here.
 */
export const FACILITY_ACCUMULATION_AREA_OPTIONS_QUERY = gql(`
  query FacilityAccumulationAreaOptions($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      accumulationAreas {
        items {
          id
          name
          type
        }
      }
    }
  }
`);

/** Headline counts for the containers list's tiles, across every facility. */
export const TENANT_ACCUMULATION_SUMMARY_QUERY = gql(`
  query TenantAccumulationSummary($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      accumulationSummary {
        activeContainerCount
        approachingDeadlineContainerCount
        overdueContainerCount
      }
    }
  }
`);

/**
 * The facility facts the add-container form shows but doesn't ask for: who the
 * container belongs to, and the generator status its accumulation deadline
 * will be computed from.
 *
 * The status comes from the accumulation summary rather than a separate
 * lookup because that resolver already owns resolving it (RCRAInfo's reported
 * status, else the one calculated from shipment history) — asking anywhere
 * else risks the preview disagreeing with the clock the container gets.
 */
export const WASTE_CONTAINER_FACILITY_CONTEXT_QUERY = gql(`
  query WasteContainerFacilityContext($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      name
      epaIdStatuses {
        epaId
        isPrimaryForFacility
        isInactive
      }
      accumulationSummary {
        federalGeneratorStatus
      }
    }
  }
`);

/** Re-exported so placement UI (this dialog, MoveContainerDialog) has a
 * single import path for the container-domain mutations it needs. */
export { MOVE_CONTAINER_MUTATION } from "../AccumulationAreas/api";

/** The compliant field set a container label renders — shared by the live
 * preview and any other view that needs the same content (export dialog,
 * regenerate confirmation). */
gql(`
  fragment LabelContentFields on LabelContent {
    generatorName
    epaId
    generatorAddress
    accumulationStartDate
    areaName
    accumulationAreaType
    containerLabel
    wasteStreamName
    classification
    wasteKind
    universalWasteCategory
    federalWasteCodes
    stateWasteCodes
    dotProperShippingName
    dotIdNumber
    dotHazardClass
    dotPackingGroup
    dotSubsidiaryHazardClass
    physicalState
    profileIsIgnitable
    profileIsCorrosive
    profileIsReactive
    profileIsToxic
    labelNote
    qrUrl
  }
`);

export const GET_CONTAINER_LABEL_QUERY = gql(`
  query GetContainerLabel($id: ID!) {
    wasteContainer(id: $id) {
      id
      containerLabel
      containerType
      capacity
      capacityUnit
      labelNote
      qrUrl
      label {
        ...LabelContentFields
      }
      facility {
        id
        name
        defaultLabelFormat
      }
    }
  }
`);

export const EXPORT_CONTAINER_LABELS_MUTATION = gql(`
  mutation ExportContainerLabels(
    $containerIds: [ID!]!
    $layout: LabelFormat!
    $fileType: LabelExportFormat!
    $branded: Boolean
    $pageSize: LabelPageSize
    $inline: Boolean
  ) {
    exportContainerLabels(
      containerIds: $containerIds
      layout: $layout
      fileType: $fileType
      branded: $branded
      pageSize: $pageSize
      inline: $inline
    ) {
      url
      layout
      fileType
    }
  }
`);

export const SET_CONTAINER_LABEL_NOTE_MUTATION = gql(`
  mutation SetContainerLabelNote($containerId: ID!, $note: String) {
    setContainerLabelNote(containerId: $containerId, note: $note) {
      id
      labelNote
      label {
        labelNote
      }
    }
  }
`);

/**
 * Everything that has to be re-read after a container changes.
 *
 * One list rather than a copy per mutation: adding a query here used to mean
 * finding every `refetchQueries` array in the container dialogs and editing
 * each by hand, and the headline tiles went stale for exactly that reason —
 * their queries existed but no mutation knew to name them.
 *
 * `GetWasteContainer` is deliberately included: a mutation fired from the
 * drawer has to refresh the container the drawer is showing, and the ones
 * fired from the list refetch a query that isn't mounted, which Apollo skips.
 */
export const CONTAINER_MUTATION_REFETCH_QUERIES = [
  "GetWasteContainer",
  "TenantWasteContainers",
  "FacilityWasteContainers",
  "FacilityAccumulationSummary",
  "TenantAccumulationSummary",
  // The area reads, for the same reason as the tiles above them. Every field
  // on `AccumulationAreaListItem` that a reader looks at — the volume rollup,
  // `containerCount`, `nearestDeadlineDate`, `oldestMovedToCentralDate` — is
  // derived from the containers in the area, so a fill, an empty, a
  // consolidation, a move or a delete all leave them stale. Not just a move:
  // that is only the case where the volume shifts between TWO areas.
  //
  // This matters now that the drawer opens over the area detail page and the
  // areas list, which stay mounted behind it — the container changes under
  // gauges that go on showing the pre-change numbers. Apollo skips refetching
  // whatever isn't mounted, so naming all three costs nothing on the surfaces
  // that don't show them.
  "GetAccumulationArea",
  "FacilityAccumulationAreas",
  "FacilityAccumulationAreasTile",
  // Same pattern for the episodic events page, whose per-event container
  // table stays mounted behind the drawer.
  "FacilityEpisodicEvents",
];
